/**
 * GENERATED FILE — DO NOT HAND-EDIT.
 * Produced by context/scripts/publish-glossary.mjs from docs/glossary.json.
 * Run: node context/scripts/publish-glossary.mjs
 */

export const GLOSSARY_ENTRIES_CONSUMER = [
  {
    "id": "authenticator-app",
    "term": "Authenticator app",
    "aliases": [
      "totp",
      "authenticator",
      "time-based one-time password"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "An app on your phone that generates a new six-digit sign-in code every 30 seconds. It works offline, and the codes are never sent over text.",
    "long": "Setting one up means scanning a QR code once. From then on the app and Vera independently compute the same six-digit code from a shared secret and the current time, so nothing has to be sent anywhere when you sign in.\n\nThat is why it is the stronger of the two options: a text message can be intercepted or redirected to a swapped SIM, while an authenticator app has nothing in transit to intercept. Any standard app works — Google Authenticator, 1Password, Authy and others.",
    "seeAlso": [
      "two-factor-authentication",
      "recovery-code"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "brand",
    "term": "Brand",
    "aliases": [
      "brands",
      "company",
      "maker"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The company that made the item and issued its certificate. Every provenance ends at the brand, because that is where the item started.",
    "long": "The brand is the origin of the record, not merely a name attached to it: the [certificate](term:certificate) exists because the brand created it and paired it to a [tag](term:tag) before the item was sold. That is what makes the [provenance](term:provenance) a chain rather than a claim — read it backwards and every completed handover leads to the company that made the thing. A brand can see that its certificates exist and how often they are scanned; it does not gain any right to the item, and ownership only ever moves by [transfer](term:transfer).",
    "seeAlso": [
      "certificate",
      "product",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "certificate",
    "term": "Certificate",
    "aliases": [
      "certificates",
      "digital certificate"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The digital record of authenticity for one physical item, owned by a person and paired with a tag.",
    "long": "A certificate stands for one individual item, not for the [product line](term:product) it belongs to: a hundred units of the same handbag are a hundred certificates against one product. It usually exists before the item is finished — certificates are minted for a whole box of [tags](term:tag) when that box is assigned to a product — and it stays [inactive](term:certificate-status-inactive) until the brand makes it live, which for most brands is the moment the item is sold. From then on it is what a scan of its tag resolves to, and what carries the item's owner, its [provenance](term:provenance) and its scan history.",
    "seeAlso": [
      "tag",
      "owner",
      "provenance",
      "activation-trigger"
    ],
    "docs": [
      "certificates/certificate-scans",
      "tags/assign-product-to-package"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "certificate-status-active",
    "term": "Active",
    "aliases": [
      "active status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate that is live for its owner. A certificate reaches this state once the item is sold, or when the brand activates it directly.",
    "long": "Active is what a certificate becomes once it is out in the world with an [owner](term:owner). Before that it sits [inactive](term:certificate-status-inactive) with the [brand](term:brand) that made it, and for most brands the change happens at the moment the item is sold and its first owner claims it. A brand can also make a certificate active directly, without waiting for a sale. It only ever happens once — a certificate that is already active is not activated again.",
    "seeAlso": [
      "certificate-status-inactive",
      "owner",
      "activation",
      "activation-trigger",
      "certificate-status-created"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "certificate-status-inactive",
    "term": "Inactive",
    "aliases": [
      "inactive status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate that exists but is not yet live for an owner, normally because the item has not been sold yet.",
    "long": "Inactive is the ordinary resting state of stock, not a fault. Certificates are created ahead of demand — in a batch, when a box of [tags](term:tag) is assigned to a [product](term:product) — so an item can be tagged, boxed and sitting in a warehouse with its certificate already waiting. Nothing about the item is wrong; it simply has no [owner](term:owner) yet. Scanning the tag of an inactive item shows the product it belongs to but no ownership, because there is none to show.",
    "seeAlso": [
      "certificate-status-active",
      "certificate-status-created",
      "certificate"
    ]
  },
  {
    "id": "certificate-status-lost",
    "term": "Lost",
    "aliases": [
      "lost status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "An item its owner has reported missing. It stays theirs, and anyone who scans the tag is shown that it has been reported lost.",
    "long": "Reporting an item lost does not give it up: ownership is unchanged and the certificate stays in your account, which is the point — you keep the claim to it while making the loss visible to whoever finds it. Because the report is attached to the [certificate](term:certificate) rather than to the [tag](term:tag), it is seen by anyone who taps the item, so a finder who scans it learns immediately that it is looked for. Marking the item found returns it to its previous state.",
    "seeAlso": [
      "certificate-status-stolen",
      "owner",
      "scan"
    ]
  },
  {
    "id": "certificate-status-stolen",
    "term": "Stolen",
    "aliases": [
      "stolen status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "An item its owner has reported stolen. It stays theirs, and anyone who scans the tag is shown that it has been reported stolen.",
    "long": "Stolen is kept separate from [Lost](term:certificate-status-lost) rather than folded into one 'missing' state, because the two mean different things to whoever is holding the item. Lost invites a finder to return it; stolen tells a prospective buyer that the person offering it has no title to it, which is the moment a resale is most likely to be stopped. Ownership does not move either way — an item cannot be [transferred](term:transfer) away from you by being taken — so the certificate remains yours until you transfer it yourself.",
    "seeAlso": [
      "certificate-status-lost",
      "owner",
      "provenance"
    ]
  },
  {
    "id": "certificate-status-transfer",
    "term": "Transfer",
    "aliases": [
      "transfer status",
      "in transfer",
      "pending transfer"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate part-way through changing hands. It stays with the current owner until the person receiving it accepts.",
    "long": "This one status covers every stage of a handover in flight — sent and unopened, waiting on the recipient, or with a cancellation requested — because from the outside they amount to the same thing: the item is spoken for but has not moved. Nothing about ownership changes while a certificate sits here, which is what makes a [transfer](term:transfer) safe to send. It resolves in one of two directions: the recipient accepts and becomes the [owner](term:owner), or the transfer is cancelled and the certificate stays exactly where it was.",
    "seeAlso": [
      "transfer",
      "gift",
      "owner",
      "provenance"
    ]
  },
  {
    "id": "certificate-view",
    "term": "Certificate view",
    "aliases": [
      "view",
      "views",
      "share link",
      "custom view",
      "public view"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A shareable link to one of your certificates, with its own setting for who may open it and how much of the item's detail it shows.",
    "long": "A view exists so that showing someone an item is not the same as giving them everything about it. Each one carries two independent controls: who can open it — anyone holding the link, or only people you have invited by email — and what it reveals, since ownership, its [history](term:provenance), sale prices and scans can each be hidden. You can keep several at once, which is the point: a link for a prospective buyer that shows provenance but not what you paid, and a private one that shows everything.",
    "seeAlso": [
      "certificate",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "gift",
    "term": "Gift",
    "aliases": [
      "gifting",
      "give"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Handing an item to someone without a sale. The certificate moves to them once they accept, and the handover joins the item's provenance.",
    "long": "A gift is a [transfer](term:transfer) with no price attached, and it behaves like every other transfer: the item stays with you until the person receiving it accepts, so an unclaimed gift can be cancelled and a mistyped address never costs anyone the item. Once accepted it becomes a permanent entry on the [provenance](term:provenance), recorded as a gift rather than a sale, which is what lets a later reader tell the difference between an item that was sold on and one that was passed to family.",
    "seeAlso": [
      "transfer",
      "sell",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "owner",
    "term": "Owner",
    "aliases": [
      "owners",
      "certificate owner"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The person a certificate currently belongs to, which changes only through a completed transfer.",
    "long": "An owner is a person, not necessarily an account: ownership is recorded when an item changes hands, so a [certificate](term:certificate) can have an owner who has never signed in — for instance one created from a store's customer list. Because ownership only moves by [transfer](term:transfer), an owner is never edited in place, and every previous owner stays on the item's [provenance](term:provenance). A certificate that has not yet reached its first owner still sits with the brand.",
    "seeAlso": [
      "transfer",
      "certificate",
      "provenance"
    ]
  },
  {
    "id": "product",
    "term": "Product",
    "aliases": [
      "products"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The model an item is made to — the design a brand makes many of. Every individual unit of it has its own certificate.",
    "long": "Product and [certificate](term:certificate) are the pair people most often read as the same thing, and the difference is one of counting. A product is the design: one entry, however many are made. A certificate is one physical unit of it, so a hundred handbags made to the same design are one product and a hundred certificates. That is why the product details on a certificate — the description, the images, the manufacture date — look the same on every unit, while the owner, the [provenance](term:provenance) and the scan history belong to the single item in your hands.",
    "seeAlso": [
      "certificate",
      "brand"
    ]
  },
  {
    "id": "provenance",
    "term": "Provenance",
    "aliases": [
      "chain of ownership",
      "history"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The full ownership history of an item, from the brand through every completed transfer.",
    "long": "Provenance reads newest-first: one entry per completed [transfer](term:transfer), each with its date and how that owner acquired the item, ending at the [brand](term:brand) where the certificate was created. Every entry also records how the handover was proven, because this is the part of a certificate a resale buyer actually reads and \"it happened\" is weaker evidence than \"the recipient accepted it and scanned the tag\". Only completed hops are entries — a cancelled or still-pending transfer never appears, and nothing is removed once it does.",
    "seeAlso": [
      "owner",
      "transfer",
      "certificate"
    ]
  },
  {
    "id": "recovery-code",
    "term": "Recovery code",
    "aliases": [
      "recovery codes",
      "backup code",
      "backup codes"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A single-use code that signs you in when you cannot reach your authenticator app or phone. Shown once when you turn on two-factor authentication.",
    "long": "Recovery codes are your way back in if your phone is lost, broken or replaced. Save them somewhere you can reach without that phone — a password manager, or printed and put away.\n\nThey are shown exactly once and cannot be retrieved later: Vera stores only a one-way hash, so nobody, including support, can read them back to you. Each code works once. If you think they have been seen by someone else, or you simply run low, you can replace the whole set with a fresh one — which immediately invalidates every code from the old set.",
    "seeAlso": [
      "two-factor-authentication",
      "authenticator-app"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "scan",
    "term": "Scan",
    "aliases": [
      "scans",
      "tap"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "One read of a tag by a phone, recorded with its time and place so owners can see where an item has been checked.",
    "long": "Every read is written down, including the ones that fail, so a [certificate](term:certificate)'s scan list is where the exceptions show up next to the ordinary taps. That is why a scan carries an outcome of its own rather than being a simple count: what the [tag](term:tag) proved varies with the tag and with the tap. Opening a certificate's web link without tapping anything is also recorded here, but marked as a link event, so the two kinds of evidence are never added together.",
    "seeAlso": [
      "tag",
      "certificate",
      "scan-status-verified"
    ],
    "docs": [
      "certificates/certificate-scans",
      "integrations/settings-installation"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "scan-status-verified",
    "term": "Verified",
    "aliases": [
      "verified scan"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A tag was tapped and confirmed genuine. This is the normal outcome, so it carries no badge in the scan list.",
    "long": "The chip answered a challenge only that chip could answer, so the tap identified the physical [tag](term:tag) rather than merely proving that a link opened. It is the overwhelmingly common outcome, which is why it is the one status with no chip: badging every ordinary row would carry no information and bury the exceptions, so the absence of a badge is itself the signal. Only tags whose family can prove identity on each tap can reach this state — a sealed tag with no per-tap proof is reported as Connected instead.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-invalid",
      "scan-status-connected",
      "scan-status-opened"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "sell",
    "term": "Sell",
    "aliases": [
      "selling",
      "sale",
      "resell",
      "resale"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Handing an item to a buyer as part of a sale, recording the price alongside the handover so it becomes part of the item's history.",
    "long": "Selling differs from [gifting](term:gift) in exactly one respect that outlives the handover: a price is recorded. That figure is what makes a [provenance](term:provenance) useful to a later buyer, since a chain of documented sales is evidence of what an item has actually been worth rather than what it is asked for. Selling through Vera is not yet available in the app; until it is, an item sold privately is handed over as a [transfer](term:transfer), which moves ownership correctly but records no price.",
    "seeAlso": [
      "gift",
      "transfer",
      "provenance"
    ]
  },
  {
    "id": "tag",
    "term": "Tag",
    "aliases": [
      "tags",
      "nfc tag"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The Vera tag attached to an item, which a phone reads to open that item's certificate.",
    "long": "A tag carries no product information of its own. It holds its own identity, and Vera resolves that identity to whichever [certificate](term:certificate) the tag is paired with. The pairing is a record held by Vera rather than something written onto the tag, so moving a tag onto a different object does not move the certificate with it — the tag simply keeps opening the certificate it was paired to. Some tags can also prove on each tap that they are the original and not a copy, which is what separates a [verified](term:scan-status-verified) scan from one that only shows a link was opened.",
    "seeAlso": [
      "certificate",
      "encode",
      "scan",
      "package"
    ],
    "docs": [
      "integrations/settings-installation",
      "tags/assign-product-to-package"
    ]
  },
  {
    "id": "transfer",
    "term": "Transfer",
    "aliases": [
      "transfers",
      "transfer out",
      "transfer in"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Moving a certificate from one owner to the next; it is pending until the receiving owner confirms.",
    "long": "A transfer is the only thing that changes a certificate's [owner](term:owner). The sender starts it and the item stays with them until the recipient accepts, so an unclaimed transfer can be cancelled and a mistyped address never costs anyone the item. How each one completed is recorded and shown on the [provenance](term:provenance): accepted from the emailed link, or accepted with a scan of the physical tag as well. For most brands, the first transfer out of the brand is also what makes the certificate [active](term:certificate-status-active).",
    "seeAlso": [
      "owner",
      "provenance",
      "activation-trigger"
    ]
  },
  {
    "id": "two-factor-authentication",
    "term": "Two-factor authentication",
    "aliases": [
      "2fa",
      "two-factor",
      "two-step verification"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A second step when you sign in. After your password or emailed code, you enter a short code from an authenticator app or a text message.",
    "long": "One factor is something you know (a password, or a code sent to your email). Two-factor authentication adds something you have — your phone. Someone who learns your password still cannot sign in without it.\n\nIt is optional for most people and can be turned on from your security settings. Once it is on, every sign-in asks for the second code; there is no way to skip it, which is the point. If you lose access to your second factor, a recovery code gets you back in.",
    "seeAlso": [
      "authenticator-app",
      "recovery-code"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "verify",
    "term": "Verify",
    "aliases": [
      "verified",
      "verification",
      "authenticate",
      "authenticity"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Checking that an item is genuine by tapping its tag with a phone. What is checked is the tag answering for itself, not a code or image that could be copied.",
    "long": "The distinction that matters is between reading something and challenging something. A printed code, a QR square or a photograph can be duplicated perfectly, so reading one proves only that a copy exists somewhere. A [tag](term:tag) that can verify is asked a question on each tap that only that individual tag can answer, and a copy of it cannot. This is why opening a [certificate](term:certificate) by following a link is recorded differently from opening it by tapping: both show you the certificate, but only one of them proves the physical item was present.",
    "seeAlso": [
      "scan",
      "tag",
      "certificate",
      "scan-status-verified"
    ]
  }
] as const
